import React, { Dispatch, FC, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { Action } from 'redux';

import lang from '../../lib/language';
import { GapperParamType, HotGappersTabEnum } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getGappers as getGappersCall } from '../../lib/store/fundamental';
import { DEFAULT_PAGE, DEFAULT_PAGE_LIST_SIZE } from '../../lib/store/fundamental/constants';
import { FundamentalCall } from '../../lib/store/fundamental/types';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import { isCallStatusError, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';
import { isMarketRegularHours } from '../../lib/util/MarketDataHelpers';

import IconHot from '../../assets/img/hot.svg';
import { Button } from '../../components';
import NoDataAvailable from '../../components/NoDataAvailable/NoDataAvailable';
import { useIsMobile } from '../../hooks';

import ContentByMarketStatus from './components/HotGappersContent/HotGappersContent';

import styles from './HotGappers.module.scss';

const HotGappers: FC<ReduxProps> = (props: ReduxProps): JSX.Element => {
  const { isGappersReady, isGappersError, gappersData, getGappers } = props;
  const isMobile = useIsMobile();
  const { marketState } = MarketStateCache.use();
  const isMarketHours = isMarketRegularHours(marketState);

  const [selectedTab, setSelectedTab] = useState<HotGappersTabEnum>(HotGappersTabEnum.GapUp);
  const isGapUpSelected = selectedTab === HotGappersTabEnum.GapUp;
  const isGapDownSelected = selectedTab === HotGappersTabEnum.GapDown;
  const hasData = !isEmpty(gappersData.content);

  useEffect(() => {
    if (!isMarketHours) {
      return;
    }

    getGappers({
      abovePreviousClose: isGapUpSelected,
      page: DEFAULT_PAGE,
      size: DEFAULT_PAGE_LIST_SIZE,
    } as GapperParamType);
  }, [selectedTab, isMarketHours]);// eslint-disable-line react-hooks/exhaustive-deps

  const getContent = (): JSX.Element => {
    if (!hasData && (isGappersReady || isGappersError)) {
      return (
        <NoDataAvailable
          className={styles.noDataToDisplay}
          isNoDataIcon
          title={lang.commonDiscover52WeekNoDataAll()}
        />
      );
    }

    return <ContentByMarketStatus isGapUpSelected={isGapUpSelected} />;
  };

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapperMob]: isMobile,
        [styles.wrapperPosition]: !isMobile,
      })}
    >
      <div className={styles.titleWrapper}>
        <span className={styles.title}>
          {lang.gappersTitle()}
          <img className={styles.icon} src={IconHot} alt="icon bank" />
        </span>
        {isMarketHours && hasData && (
          <div className={styles.tabsWrapper}>
            <div
              className={classNames(styles.tabs, {
                [styles.tabMobWidth]: isMobile,
              })}
            >
              <Button
                className={classNames(styles.tab, {
                  [styles.tabActive]: isGapUpSelected,
                  [styles.tabMob]: isMobile,
                  [styles.tabWebWidth]: !isMobile,
                  [styles.tabMobWidth]: isMobile,
                })}
                disabled={!isGappersReady}
                onClick={() => setSelectedTab(HotGappersTabEnum.GapUp)}
              >
                {lang.gappersTitleGapUp()}
              </Button>

              <Button
                className={classNames(styles.tab, {
                  [styles.tabActive]: isGapDownSelected,
                  [styles.tabMob]: isMobile,
                  [styles.tabWebWidth]: !isMobile,
                  [styles.tabMobWidth]: isMobile,
                })}
                disabled={!isGappersReady}
                onClick={() => setSelectedTab(HotGappersTabEnum.GapDown)}
              >
                {lang.gappersTitleGapDown()}
              </Button>

            </div>
          </div>
        )}
      </div>
      {getContent()}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isGappersReady: isCallStatusReady(FundamentalCall.getGappers, state.fundamental.statusByCall),
  isGappersError: isCallStatusError(FundamentalCall.getGappers, state.fundamental.statusByCall),
  gappersData: state.fundamental.gappersData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getGappers: (params: GapperParamType) => dispatch(getGappersCall(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(HotGappers);
